import type { ReactNode } from "react";
import { useState } from "react";
import { SearchLg, ChevronLeft, ChevronRight } from "@untitledui/icons";
import { cx } from "@/utils/cx";
import { MobileNavigationHeader } from "../base-components/mobile-header";
import { NavAccountCard } from "../base-components/nav-account-card";
import { NavItemBase } from "../base-components/nav-item";
import { NavList } from "../base-components/nav-list";
import type { NavItemType } from "../config";
import { useCommandMenu } from "@/components/application/command-menu/use-command-menu";
import { SignOutConfirmModal } from "../sign-out-confirm-modal";
import { useAccount } from "@/providers/account-provider";
import { isRouteActive } from "@/utils/route-matcher";
import { VersionFooter } from "@/components/base/version-footer/version-footer";
import { useSidebar } from "@/providers/sidebar-provider";

interface SidebarNavigationProps {
    /** URL of the currently active item. */
    activeUrl?: string;
    /** List of items to display. */
    items: NavItemType[];
    /** List of footer items to display. */
    footerItems?: NavItemType[];
    /** Feature card to display. */
    featureCard?: ReactNode;
    /** Whether to show the account card. */
    showAccountCard?: boolean;
    /** Whether to hide the right side border. */
    hideBorder?: boolean;
    /** Additional CSS classes to apply to the sidebar. */
    className?: string;
}

export const SidebarNavigationSimple = ({
    activeUrl,
    items,
    footerItems = [],
    featureCard,
    showAccountCard = true,
    hideBorder = false,
    className,
}: SidebarNavigationProps) => {
    const MAIN_SIDEBAR_WIDTH = 260;
    const COLLAPSED_SIDEBAR_WIDTH = 72;
    const { open } = useCommandMenu();
    const { signOut } = useAccount();
    const [showSignOutModal, setShowSignOutModal] = useState(false);
    const { isCollapsed, toggleSidebar } = useSidebar();
    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    const currentWidth = isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : MAIN_SIDEBAR_WIDTH;

    // Verifica se un item è il logout
    const isLogoutItem = (item: NavItemType) => {
        const label = item.label?.toLowerCase() || '';
        const href = item.href?.toLowerCase() || '';
        return label.includes('logout') || label.includes('log out') ||
            href.includes('/auth/login') || href.includes('/logout');
    };

    const handleFooterItemClick = (item: NavItemType, e: React.MouseEvent) => {
        if (isLogoutItem(item)) {
            e.preventDefault();
            e.stopPropagation();
            setShowSignOutModal(true);
        }
    };

    const handleSearchClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Apri sempre quando si clicca (forza l'apertura anche se preventata)
        open(true);
    };

    const content = (
        <aside
            style={
                {
                    "--width": `${currentWidth}px`,
                } as React.CSSProperties
            }
            className={cx(
                "flex h-full w-full max-w-full flex-col justify-between overflow-auto bg-primary pt-4 lg:w-(--width) lg:pt-6 transition-all duration-300",
                !hideBorder && "border-secondary md:border-r",
                className,
            )}
        >
            <div className={cx("flex flex-col gap-5", isCollapsed ? "px-2 lg:px-2" : "px-4 lg:px-5")}>
                <div className="flex items-center justify-between">
                    <div className={cx("flex items-center gap-2", isCollapsed && "justify-center w-full")}>
                        <img
                            src={isCollapsed ? "/images/logo.svg" : "/images/logo-full.svg"}
                            alt="Eve Mind"
                            className="w-full shrink-0 object-contain"
                        />
                    </div>
                </div>
                {!isCollapsed ? (
                    <button
                        type="button"
                        onClick={handleSearchClick}
                        className={cx(
                            "relative flex w-full cursor-pointer flex-row place-content-center place-items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary ring-inset transition-shadow duration-100 ease-linear",
                            "hover:ring-brand/80 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand",
                        )}
                        aria-label="Search"
                    >
                        <SearchLg className="pointer-events-none absolute left-3 size-5 text-fg-quaternary" aria-hidden />
                        <span className="m-0 w-full bg-transparent py-2 pl-10 pr-20 text-left text-md text-placeholder">
                            Search
                        </span>
                        <div
                            className="pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8 pr-2.5"
                            aria-hidden
                        >
                            <span className="rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary ring-inset select-none">
                                ⌘K
                            </span>
                        </div>
                    </button>
                ) : (
                    <button
                        onClick={handleSearchClick}
                        className="flex items-center justify-center p-2 rounded-lg hover:bg-secondary transition-colors"
                        aria-label="Search"
                    >
                        <SearchLg className="size-5 text-tertiary" />
                    </button>
                )}
            </div>

            <NavList activeUrl={activeUrl} items={items} isCollapsed={isCollapsed} />

            <div className={cx("mt-auto flex flex-col gap-4 py-4 lg:py-6", isCollapsed ? "px-2" : "px-2 lg:px-4")}>

                {!isCollapsed && featureCard}

                {footerItems.length > 0 && (
                    <ul className="flex flex-col">
                        {footerItems.map((item) => (
                            <li key={item.label} className="py-0.5">
                                <NavItemBase
                                    badge={!isCollapsed ? item.badge : undefined}
                                    icon={item.icon}
                                    href={item.href}
                                    type="link"
                                    current={isRouteActive(item.href, activeUrl)}
                                    onClick={(e) => handleFooterItemClick(item, e)}
                                    isCollapsed={isCollapsed}
                                >
                                    {!isCollapsed && item.label}
                                </NavItemBase>
                            </li>
                        ))}
                    </ul>
                )}

                {showAccountCard && !isCollapsed && <NavAccountCard />}

                {/* Collapse/Expand Toggle Button */}
                <button
                    onClick={toggleSidebar}
                    className={cx(
                        "hidden lg:flex items-center justify-center p-2 rounded-lg hover:bg-secondary transition-colors text-tertiary hover:text-primary",
                        isCollapsed ? "mx-auto" : "ml-auto"
                    )}
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                >
                    {isCollapsed ? <ChevronRight className="size-5" /> : <ChevronLeft className="size-5" />}
                </button>

                {showAccountCard && !isCollapsed && <VersionFooter />}
            </div>
        </aside>
    );

    return (
        <>
            {/* Mobile header navigation */}
            <MobileNavigationHeader>{content}</MobileNavigationHeader>

            {/* Desktop sidebar navigation */}
            <div
                className={cx(
                    "hidden lg:fixed lg:left-0 lg:flex",
                    hasEnvironmentBanner ? "lg:top-7 lg:bottom-0" : "lg:inset-y-0"
                )}
            >
                {content}
            </div>

            {/* Placeholder to take up physical space because the real sidebar has `fixed` position. */}
            <div
                style={{
                    paddingLeft: currentWidth,
                }}
                className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block transition-all duration-300"
            />

            {/* Sign Out Confirmation Modal */}
            <SignOutConfirmModal
                isOpen={showSignOutModal}
                onClose={() => setShowSignOutModal(false)}
                onConfirm={() => {
                    signOut();
                    setShowSignOutModal(false);
                }}
            />
        </>
    );
};
